import React from 'react';

import Validation from 'modules/react-validation';

// Adds disableOnFormValidationErrors and hideOnFormValidationErrors
// to an existing component
function withFormValidationProps<P, S> (WrappedComponent: React.ComponentClass<P, S>) {
  let displayName = WrappedComponent.displayName || WrappedComponent.name || 'withFormValidationProps';
  displayName = displayName !== 'withFormValidationProps'
    ? 'withFormValidationProps(' + displayName + ')'
    : displayName;

  type Props = {
    validations?: string[],
    disabled?: boolean,
    disableOnFormValidationErrors?: boolean,
    hideOnFormValidationErrors?: boolean
  };

  type DefaultProps = Required<Pick<Props, 'disableOnFormValidationErrors' | 'hideOnFormValidationErrors'>>;

  type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;
  
  class WithFormValidationProps extends React.Component<P & Props> {
    static defaultProps: DefaultProps = {
      disableOnFormValidationErrors: false,
      hideOnFormValidationErrors: false
    };

    static displayName = displayName;

    static contextType = Validation.contexts.ValidationContext;
    context!: React.ContextType<typeof Validation.contexts.ValidationContext>
    
    override render (): React.ReactNode {
      const {
        disableOnFormValidationErrors,
        hideOnFormValidationErrors
      } = this.props;

      // Strip disableOnFormValidationErrors and hideOnFormValidationErrors
      // from the props we pass on to WrappedComponents.
      let props: P & Props = {...this.props};
      delete props.disableOnFormValidationErrors;
      delete props.hideOnFormValidationErrors;

      if (!props.disabled && disableOnFormValidationErrors) {
        // set props.disabled if there are form validation errors
        props = Object.assign({}, props, {
          disabled: ((this.context.errors != null) && (Object.keys(this.context.errors).length > 0))
        });
      }
      if (hideOnFormValidationErrors &&
          (this.context.errors != null) &&
          (Object.keys(this.context.errors).length > 0)) {
        // don't render the component if there are form validation errors
        return null;
      }

      // pass through the modified props
      return <WrappedComponent {...props} />;
    }
  }

  return WithFormValidationProps;
}

export default withFormValidationProps;
